import React, { useEffect, useRef, useState } from 'react';
import { Application } from '@feathersjs/feathers';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import {
  Alert,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  InputAdornment,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import moment from 'moment';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import {
  ContestShapefile,
  ContestShapefileStatus,
  getContestShapefileErrorMessage
} from '../../app/feathers/contest-shapefiles';
import { District } from '../../app/feathers/districts/District';
import {
  DataDetailToolbar,
  LoadingSpinner,
  PaginationControl,
  PaginationStatus,
  SearchTextField,
  StepTracker
} from '../../components';
import { isPermitted, useFeathers } from '../../app/util';
import { buildContestShapefileQuery } from './query';

const PAGE_SIZE = 10;
const TERMINAL_STATUSES = ['error', 'canceled'];
const STAGE_OPTIONS: Array<{ value: ContestShapefileStatus; label: string }> = [
  { value: 'processing', label: 'Processing' },
  { value: 'labeling', label: 'Matching' },
  { value: 'needs-review', label: 'Needs review' },
  { value: 'creating', label: 'Creating' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' }
];

const getWorkflowState = (record: ContestShapefile) => {
  const effectiveStatus = TERMINAL_STATUSES.includes(record.status)
    ? [...record.statusEntries]
      .reverse()
      .find(entry => !TERMINAL_STATUSES.includes(entry.status))?.status || 'processing'
    : record.status;

  const currentStep = effectiveStatus === 'processing'
    ? 0
    : effectiveStatus === 'labeling'
      ? 1
      : effectiveStatus === 'needs-review'
        ? 2
        : 3;

  return {
    currentStep,
    inProgressStep: TERMINAL_STATUSES.includes(record.status) || record.status === 'needs-review' || record.status === 'completed'
      ? -1
      : currentStep,
    completedThrough: record.status === 'completed' ? 3 : currentStep - 1
  };
};

const ContestShapefiles: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const { key: electionKey } = useParams<{ key: string }>();
  const history = useHistory();
  const user = useSelector((state: any) => state.user);
  const election = useSelector((state: any) => state.elections?.byKey[electionKey]);
  const [records, setRecords] = useState<ContestShapefile[]>([]);
  const [countyNames, setCountyNames] = useState<Record<string, string>>({});
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<ContestShapefileStatus | ''>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const canCreate = isPermitted(user, ['editor', 'admin', 'super-admin', 'dms-shapefile']);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    if (!feathers) return;
    let current = true;
    const load = async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const response = await feathers.getService('contest-shapefiles').find({
          query: buildContestShapefileQuery({
            electionKey,
            stage,
            search: debouncedSearch,
            page,
            pageSize: PAGE_SIZE
          })
        });
        if (!current) return;
        setRecords(response.data);
        setTotal(response.total);

        const countyIds = Array.from(new Set(response.data.map(record => `${record.county}`)));
        if (countyIds.length) {
          const countyResponse = await feathers.getService('districts').find({
            query: {
              _id: { $in: countyIds },
              $limit: countyIds.length,
              $select: ['_id', 'name', 'longName', 'shortName', 'matchName']
            }
          });
          if (!current) return;
          setCountyNames(countyResponse.data.reduce((names, county: District) => ({
            ...names,
            [county._id]: county.name || county.longName || county.shortName || county.matchName
          }), {} as Record<string, string>));
        } else {
          setCountyNames({});
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (current && !silent) setLoading(false);
      }
    };
    load();
    const interval = window.setInterval(() => load(true), 10000);
    return () => {
      current = false;
      window.clearInterval(interval);
    };
  }, [debouncedSearch, electionKey, feathers, page, stage]);

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}` } }}
        navTree={[
          { text: 'Elections', to: { pathname: '/elections' } },
          { text: election?.name || 'Election', to: { pathname: `/elections/${electionKey}` } },
          { text: 'Contest Shapefiles' }
        ]}
        actionButtonsComponent={canCreate ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => history.push(`/elections/${electionKey}/contest-shapefiles/new`)}
          >
            New Upload
          </Button>
        ) : null}
      />

      <Filters>
        <SearchTextField
          placeholder="Search by county"
          value={search}
          inputRef={searchInputRef}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          size="small"
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear county search"
                  edge="end"
                  onClick={() => {
                    setSearch('');
                    setPage(0);
                  }}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="contest-shapefile-stage-filter-label">Stage</InputLabel>
          <Select
            labelId="contest-shapefile-stage-filter-label"
            value={stage}
            label="Stage"
            onChange={(event) => {
              setStage(event.target.value as ContestShapefileStatus | '');
              setPage(0);
            }}
          >
            <MenuItem value="">All stages</MenuItem>
            {STAGE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Filters>

      <Content>
        <TableHeader>
          <Typography variant="h5">County</Typography>
          <Typography variant="h5">Progress</Typography>
          <Typography variant="h5">Updated</Typography>
        </TableHeader>
        {loading ? (
          <Empty><LoadingSpinner width={64} /></Empty>
        ) : records.length === 0 ? (
          <Empty>
            <Typography color="textSecondary">
              {debouncedSearch
                ? `No contest shapefiles found matching "${debouncedSearch}".`
                : 'No contest shapefiles found for this election.'}
            </Typography>
            {canCreate && !debouncedSearch && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => history.push(`/elections/${electionKey}/contest-shapefiles/new`)}
              >
                Upload First Shapefile
              </Button>
            )}
          </Empty>
        ) : records.map(record => {
          const workflow = getWorkflowState(record);
          const recordError = getContestShapefileErrorMessage(record);
          return (
            <TableRow
              key={record._id}
              to={`/elections/${electionKey}/contest-shapefiles/${record._id}`}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {countyNames[record.county] || 'Unknown county'}
              </Typography>
              <WorkflowColumn>
                <WorkflowProgress>
                  <StepTracker
                    compact
                    currentStep={workflow.currentStep}
                    steps={['Processed', 'Matched', 'Verified', 'Created'].map((name, index) => ({
                      name,
                      isComplete: index <= workflow.completedThrough,
                      isInProgress: index === workflow.inProgressStep
                    }))}
                    style={{ justifyContent: 'flex-start' }}
                  />
                  {TERMINAL_STATUSES.includes(record.status) && (
                    <Chip
                      size="small"
                      color={record.status === 'error' ? 'error' : 'default'}
                      label={record.status}
                    />
                  )}
                </WorkflowProgress>
                {recordError && <Alert severity="error">{recordError}</Alert>}
              </WorkflowColumn>
              <Typography variant="body2">
                {record.updatedAt ? moment(record.updatedAt).format('MMM D, YYYY h:mm A') : '--'}
              </Typography>
            </TableRow>
          );
        })}
      </Content>

      {total > PAGE_SIZE && (
        <PaginationRow>
          <PaginationStatus
            activePage={page + 1}
            items={total}
            itemsPerPage={PAGE_SIZE}
            itemName="contest shapefile"
          />
          <PaginationControl
            activePage={page + 1}
            items={total}
            itemsPerPage={PAGE_SIZE}
            onChange={(nextPage: number) => setPage(nextPage - 1)}
          />
        </PaginationRow>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: calc(100% - 48px);
  padding: 24px;
`;

const Content = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(480px, 2fr) 220px;
  gap: 24px;
  padding: 16px 24px;
  background: #f7f9fe;
`;

const TableRow = styled(Link)`
  display: grid;
  grid-template-columns: minmax(180px, 1fr) minmax(480px, 2fr) 220px;
  gap: 24px;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #eee;
  color: inherit;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background: #f7f9fe;
  }
`;

const WorkflowProgress = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const WorkflowColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px;
`;

const PaginationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

export default ContestShapefiles;
